import { Link } from "react-router-dom";
import { Star, MessagesSquare, TrendingUp, ShieldCheck, ArrowRight, MessageCircle } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { FadeUp, MaskedLines, Eyebrow } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Stats from "@/components/Stats";

const MARQUEE_ITEMS = [
  "Rendiamo iconico il tuo brand.",
  "Facciamo crescere la tua audience e moltiplichiamo i tuoi ricavi.",
  "Gestiamo le tue vendite h24.",
  "Blindiamo la tua privacy e i tuoi contenuti.",
  "Scaliamo il tuo business, senza farti perdere tempo.",
  "Tiriamo fuori il tuo potenziale, gestendo ogni singolo dettaglio.",
];

const SERVICES = [
  {
    icon: <Star size={20} />,
    title: "Creator Management",
    text: "Gestione a 360° della tua presenza digitale: regia strategica, piani editoriali multi-canale, shooting concept e posizionamento esclusivo del tuo brand su tutte le piattaforme.",
    testid: "service-card-management",
  },
  {
    icon: <MessagesSquare size={20} />,
    title: "Sales & Chat Management",
    text: "Team di vendita dedicato attivo 24/7. Massimizziamo le conversioni e l'ARPU attraverso tecniche di vendita empatica, upselling mirato e fidelizzazione della tua audience.",
    testid: "service-card-sales",
  },
  {
    icon: <TrendingUp size={20} />,
    title: "Growth & Social Traffic",
    text: "Acquisizione traffico e crescita organica su Instagram, TikTok e canali proprietari. Creiamo funnel di conversione guidati dai dati per trasformare i follower in utenti paganti.",
    testid: "service-card-growth",
  },
  {
    icon: <ShieldCheck size={20} />,
    title: "Privacy & Brand Protection",
    text: "Tutela totale dell'anonimato, contratti blindati (NDA), tracciamento e rimozione immediata delle diffusioni non autorizzate per proteggere la tua reputazione e i tuoi contenuti.",
    testid: "service-card-privacy",
  },
];

const METHOD = [
  { n: "01", title: "Onboarding & Blindatura Privacy", text: "Firma degli accordi di riservatezza (NDA), messa in sicurezza degli accessi e definizione rigorosa dei parametri di anonimato e tutela della tua identità prima di qualsiasi operatività." },
  { n: "02", title: "Discovery & Posizionamento Iconico", text: "Analisi del tuo profilo, studio del target di riferimento e progettazione di un'identità visiva esclusiva che distingua il tuo brand dalla concorrenza." },
  { n: "03", title: "Regia Contenuti & Crescita Social", text: "Pianificazione editoriale multi-canale (Instagram, TikTok), concept visivi per gli shooting e strategie di acquisizione traffico per attrarre costantemente nuovo pubblico in target." },
  { n: "04", title: "Architettura di Vendita & Chat 24/7", text: "Integrazione del team dedicato di vendita: presidio costante dei messaggi, script personalizzati sul tuo tono di voce e gestione dei funnel ad alta conversione." },
  { n: "05", title: "Analisi Dati & Scalabilità Continua", text: "Monitoraggio settimanale delle metriche, ottimizzazione dei prezzi di vendita e sviluppo di nuove linee di ricavo per scalare il fatturato mese dopo mese." },
];

export default function Home() {
  const { openContact } = useSite();

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="hero-section">
        <div className="pointer-events-none absolute -left-40 top-1/3 h-[420px] w-[420px] rounded-full bg-gold/[0.04] blur-[130px]" />
        <div className="pointer-events-none absolute -right-32 top-16 h-[320px] w-[320px] rounded-full bg-gold/[0.05] blur-[120px]" />
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-32 sm:px-8 lg:pb-20 lg:pt-44">
          <div className="max-w-3xl">
            <FadeUp>
              <Eyebrow>Società italiana regolarmente registrata</Eyebrow>
            </FadeUp>
            <MaskedLines
              className="mt-8"
              delay={0.15}
              lineClassName="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-[#F5F3EE]"
              lines={[
                "TI TRASFORMIAMO",
                <>
                  IN UN'<span className="gold-text gold-text-animated italic">ICONA.</span>
                </>,
              ]}
            />
            <FadeUp delay={0.5} className="mt-8 max-w-xl">
              <p className="text-base sm:text-lg leading-relaxed text-[#969696]">
                Una struttura manageriale d'élite al tuo fianco: proteggiamo la tua privacy, valorizziamo la tua
                immagine e scaliamo il tuo business digitale.
              </p>
            </FadeUp>
            <FadeUp delay={0.65} className="mt-12 flex flex-col gap-4 sm:flex-row">
              <button
                data-testid="hero-become-creator-cta"
                onClick={() => openContact("creator")}
                className="inline-flex items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-bold uppercase tracking-[0.18em] text-[#080808] transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(229,196,90,0.25)]"
              >
                <MessageCircle size={16} /> Candidati come Creator
              </button>
            </FadeUp>
            <FadeUp delay={0.8} className="mt-14 flex items-center gap-4">
              <span className="h-px w-10 bg-gold/50" />
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#969696]">
                IEM — Iconic Elite Management S.R.L.S.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      <Stats />

      {/* SERVIZI */}
      <section className="py-14 lg:py-20" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <FadeUp>
            <Eyebrow>Cosa facciamo</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight text-[#F5F3EE]">
              Un sistema completo. <span className="italic text-gold">Un unico partner.</span>
            </h2>
          </FadeUp>

          <div className="mt-12 grid gap-px border border-[#1A1A1A] bg-[#1A1A1A] sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.08} className="group bg-[#0b0b0b] p-8 lg:p-10 transition-colors duration-500 hover:bg-[#111111]">
                <div data-testid={s.testid}>
                  <div className="flex h-11 w-11 items-center justify-center border border-gold/25 text-gold transition-colors duration-500 group-hover:border-gold/60">
                    {s.icon}
                  </div>
                  <h3 className="mt-7 font-serif text-xl sm:text-2xl text-[#F5F3EE]">{s.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#969696]">{s.text}</p>
                </div>
              </FadeUp>
            ))}
            <FadeUp delay={0.32} className="relative bg-[#0b0b0b] p-8 lg:p-10 sm:col-span-2 lg:col-span-2 overflow-hidden">
              <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-gold/[0.06] blur-[90px]" />
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">Il posizionamento</div>
              <h3 className="mt-6 max-w-lg font-serif text-xl sm:text-2xl lg:text-3xl leading-snug text-[#F5F3EE]">
                Oltre la singola piattaforma: un ecosistema di business.
              </h3>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-[#969696]">
                OnlyFans rappresenta il motore di monetizzazione ad alta conversione in cui operiamo con standard
                manageriali e team dedicati h24. La nostra visione, però, va oltre: sviluppiamo la tua identità su
                Instagram, TikTok e canali riservati, costruendo un asset digitale solido, indipendente e duraturo
                nel tempo.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* METODO */}
      <section className="border-t border-[#1A1A1A] py-14 lg:py-20" data-testid="method-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <FadeUp>
            <Eyebrow>Il metodo IEM</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F5F3EE]">
              Cinque fasi. <span className="italic text-gold">Metodo rigoroso, zero improvvisazione.</span>
            </h2>
          </FadeUp>
          <div className="mt-12">
            {METHOD.map((m, i) => (
              <FadeUp key={m.n} delay={i * 0.06}>
                <Link
                  to="/metodo-privacy"
                  data-testid={`method-step-${m.n}`}
                  className="group grid grid-cols-[auto_1fr] items-baseline gap-6 border-t border-[#1A1A1A] py-9 transition-colors duration-300 last:border-b hover:bg-[#0d0d0d] sm:grid-cols-[100px_1fr_1fr] lg:gap-12"
                >
                  <span className="font-serif text-2xl text-gold/70 transition-colors group-hover:text-gold">{m.n}</span>
                  <h3 className="font-serif text-xl sm:text-2xl text-[#F5F3EE]">{m.title}</h3>
                  <p className="col-span-2 mt-3 sm:col-span-1 sm:mt-0 text-sm leading-relaxed text-[#969696]">{m.text}</p>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PRIVACY BAND */}
      <section className="py-14 lg:py-20" data-testid="privacy-band">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <FadeUp className="relative overflow-hidden border border-[#1A1A1A] bg-[#0d0d0d] p-8 sm:p-10 lg:p-16 grain">
            <div className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-gold/[0.06] blur-[100px]" />
            <div className="relative grid items-center gap-10 lg:grid-cols-[auto_1fr_auto]">
              <div className="flex h-16 w-16 items-center justify-center border border-gold/30 text-gold">
                <ShieldCheck size={26} />
              </div>
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F5F3EE]">
                  La tua privacy è il nostro <span className="italic text-gold">protocollo.</span>
                </h2>
                <p className="mt-5 max-w-2xl text-sm sm:text-base leading-relaxed text-[#969696]">
                  NDA, dati crittografati, accessi riservati e tutela dell'identità. Ogni collaborazione con IEM —
                  Iconic Elite Management S.R.L.S. è protetta da standard di riservatezza assoluta.
                </p>
              </div>
              <Link
                data-testid="privacy-band-cta"
                to="/metodo-privacy"
                className="inline-flex items-center gap-3 border border-gold/50 px-7 py-4 text-[12px] font-semibold tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-[#080808]"
              >
                METODO & PRIVACY <ArrowRight size={14} />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="relative overflow-hidden border-t border-[#1A1A1A] py-16 lg:py-24 grain" data-testid="final-cta">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[380px] w-[680px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/[0.05] blur-[130px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center sm:px-8">
          <FadeUp>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">
              IEM — Iconic Elite Management S.R.L.S.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-7 font-serif text-3xl sm:text-4xl lg:text-6xl leading-tight text-[#F5F3EE]">
              Meriti una struttura <span className="gold-text italic">all'altezza delle tue ambizioni.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-[#969696]">
              Una gestione manageriale completa al tuo servizio per consacrare il tuo status di icona e massimizzare
              i tuoi guadagni, portando il tuo business al livello successivo.
            </p>
          </FadeUp>
          <FadeUp delay={0.3} className="mt-12 flex justify-center">
            <button
              data-testid="final-become-creator-cta"
              onClick={() => openContact("creator")}
              className="inline-flex items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-bold uppercase tracking-[0.18em] text-[#080808] transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(229,196,90,0.25)]"
            >
              <MessageCircle size={16} /> Candidati come Creator
            </button>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
